import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Photos = createContext({ models: [], update: () => {} });
export function ModelPhotosProvider({ children }) {
  const { token } = useAuth();
  const [models, setModels] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!token) { setModels([]); return; }
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/models/photos`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : []).then(rows => { if (!cancelled) setModels(rows); }).catch(() => {});
    return () => { cancelled = true; };
  }, [token]);
  const update = model => setModels(rows => [...rows.filter(row => String(row.id) !== String(model.id)), model]);
  return <Photos.Provider value={{ models, update }}>{children}</Photos.Provider>;
}

export default function ModelAvatar({ modelId, name, color, src, size = 28, style }) {
  const { models } = useContext(Photos);
  const model = models.find(row => modelId != null ? String(row.id) === String(modelId) : row.name?.toLowerCase() === name?.toLowerCase());
  const url = model?.profile_photo_url || src;
  const [failed, setFailed] = useState(null);
  return <span title={name || model?.name} style={{ width: size, height: size, minWidth: size, flexShrink: 0, borderRadius: '50%', overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle', background: color || model?.color || 'var(--brand)', color: '#fff', fontWeight: 700, fontSize: Math.max(10, size * .4), ...style }}>
    {url && failed !== url ? <img src={url} alt={`${name || model?.name || 'Model'} profile`} onError={() => setFailed(url)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : (name || model?.name || '?').slice(0,1).toUpperCase()}
  </span>;
}

// One upload path for every place a photo can be set. Saving pushes the new
// photo into the shared provider, so every ModelAvatar on every page picks it
// up immediately — no reload, no per-page refetch.
function usePhotoUpload(model, onSaved) {
  const { apiFetch } = useAuth();
  const { update } = useContext(Photos);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const upload = async event => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file) return;
    setError(''); setBusy(true);
    let bitmap;
    try {
      if (!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size > 15 * 1024 * 1024) throw new Error('Choose a JPG, PNG or WebP photo under 15 MB.');
      bitmap = await createImageBitmap(file);
      const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
      const side = Math.min(bitmap.width, bitmap.height);
      canvas.getContext('2d').drawImage(bitmap, (bitmap.width-side)/2, (bitmap.height-side)/2, side, side, 0,0,512,512);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .88));
      if (!blob) throw new Error('Could not read this photo. Try another image.');
      const form = new FormData(); form.append('photo', blob, 'profile.jpg');
      const response = await apiFetch(`/api/models/${model.id}/photo`, { method: 'POST', body: form });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Photo could not be saved.');
      const saved = { ...model, ...result }; update(saved); onSaved?.(saved);
    } catch (err) { setError(err.message || 'Photo could not be saved.'); }
    finally { bitmap?.close(); setBusy(false); }
  };
  return { upload, busy, error };
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

// The avatar is the upload button. A text link under the name was the only way
// in before, and it was easy to miss entirely — the photo is the thing you are
// changing, so that is where you click. The camera badge says it is editable.
export function EditableModelAvatar({ model, color, size = 52, onSaved }) {
  const { upload, busy, error } = usePhotoUpload(model, onSaved);
  const badge = Math.max(18, Math.round(size * 0.38));
  return <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
    <label title={model.profile_photo_url ? 'Change profile photo' : 'Add profile photo'} style={{ position: 'relative', display: 'inline-flex', cursor: busy ? 'wait' : 'pointer', borderRadius: '50%' }}>
      <ModelAvatar modelId={model.id} name={model.name} color={color} src={model.profile_photo_url} size={size} style={{ opacity: busy ? .5 : 1, transition: 'opacity .2s' }} />
      <span aria-hidden="true" style={{ position: 'absolute', right: -2, bottom: -2, width: badge, height: badge, borderRadius: '50%', background: 'var(--brand)', color: '#fff', border: '2px solid var(--s1, #101012)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={badge * .55} height={badge * .55} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
      </span>
      <input aria-label={`Upload profile photo for ${model.name}`} type="file" accept={ACCEPT} disabled={busy} onChange={upload} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
    </label>
    {error && <span role="alert" style={{ color: 'var(--error)', fontSize: 11, marginTop: 6, maxWidth: 160, textAlign: 'center' }}>{error}</span>}
  </span>;
}
